import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Building2, LogOut, Search, User, Briefcase, MapPin, Shield, 
  CheckCircle, XCircle, Clock, Ban, Eye, Filter, ChevronDown,
  AlertTriangle, FileText, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminUserControl() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [users, setUsers] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filter states - initialize from URL query parameters
  const [filters, setFilters] = useState(() => {
    return {
      user_type: searchParams.get('user_type') || 'all',
      trade: searchParams.get('trade') || '',
      location: searchParams.get('location') || '',
      verification_status: searchParams.get('verification_status') || '',
      account_status: searchParams.get('account_status') || 'all',
      search: searchParams.get('search') || ''
    };
  });
  const [showFilters, setShowFilters] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 25;

  useEffect(() => {
    checkAuth();
    fetchTrades();
  }, []);

  useEffect(() => {
    if (currentAdmin) {
      fetchUsers();
    }
  }, [filters, offset, currentAdmin]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      setCurrentAdmin(response.data);
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trades`);
      setTrades(response.data);
    } catch (error) {
      console.error('Failed to fetch trades');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.user_type !== 'all') params.append('user_type', filters.user_type);
      if (filters.trade) params.append('trade', filters.trade);
      if (filters.location) params.append('location', filters.location);
      if (filters.verification_status) params.append('verification_status', filters.verification_status);
      if (filters.account_status !== 'all') params.append('account_status', filters.account_status);
      if (filters.search) params.append('search', filters.search);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await axios.get(`${BACKEND_URL}/api/admin/user-search?${params.toString()}`, {
        withCredentials: true
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    fetchUsers();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setOffset(0);
  };

  const clearFilters = () => {
    setFilters({
      user_type: 'all',
      trade: '',
      location: '',
      verification_status: '',
      account_status: 'all',
      search: ''
    });
    setOffset(0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      suspended: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      banned: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' }
    };
    return badges[status] || badges.active;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  // Stats
  const activeCount = users.filter(u => (u.account_status || 'active') === 'active' && u.is_active !== false).length;
  const suspendedCount = users.filter(u => u.account_status === 'suspended').length;
  const bannedCount = users.filter(u => u.account_status === 'banned').length;

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="bg-[#12121a] border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-orange-500" />
              <span className="text-xl font-bold text-white">User Control Panel</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-400 hover:text-white text-sm"
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate('/admin/reports-queue')}
                className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
              >
                <AlertTriangle className="w-4 h-4" /> Reports
              </button>
              <button
                onClick={() => navigate('/admin/verification-queue')}
                className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
              >
                <FileText className="w-4 h-4" /> Verifications
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Found</p>
                <p className="text-2xl font-bold text-white">{total}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Active</p>
                <p className="text-2xl font-bold text-white">{activeCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Suspended</p>
                <p className="text-2xl font-bold text-white">{suspendedCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <Ban className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Banned</p>
                <p className="text-2xl font-bold text-white">{bannedCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4 mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Search Bar */}
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                  data-testid="search-input"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
                  showFilters ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' : 'bg-[#0a0a0f] border-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                <ChevronDown className={`w-4 h-4 transition ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
                data-testid="search-btn"
              >
                Search
              </button>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-gray-800">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">User Type</label>
                  <select
                    value={filters.user_type}
                    onChange={(e) => handleFilterChange('user_type', e.target.value)}
                    className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white"
                    data-testid="filter-user-type"
                  >
                    <option value="all">All Users</option>
                    <option value="employer">Employers</option>
                    <option value="tradesperson">Tradespeople</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Trade</label>
                  <select
                    value={filters.trade}
                    onChange={(e) => handleFilterChange('trade', e.target.value)}
                    className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white"
                    data-testid="filter-trade"
                  >
                    <option value="">All Trades</option>
                    {trades.map(trade => (
                      <option key={trade.name} value={trade.name}>{trade.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Location</label>
                  <input
                    type="text"
                    placeholder="Enter location..."
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white"
                    data-testid="filter-location"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Verification</label>
                  <select
                    value={filters.verification_status}
                    onChange={(e) => handleFilterChange('verification_status', e.target.value)}
                    className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white"
                    data-testid="filter-verification"
                  >
                    <option value="">All</option>
                    <option value="verified">Verified</option>
                    <option value="unverified">Unverified</option>
                    <option value="pending">Pending Review</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Account Status</label>
                  <select
                    value={filters.account_status}
                    onChange={(e) => handleFilterChange('account_status', e.target.value)}
                    className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white"
                    data-testid="filter-status"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="banned">Banned</option>
                  </select>
                </div>
                <div className="col-span-2 md:col-span-5 flex justify-end">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-gray-400 hover:text-white text-sm"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Users Table */}
        <div className="bg-[#12121a] border border-gray-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
            </div>
          ) : users.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0a0a0f]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Trade/Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Verification</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Joined</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {users.map((user) => {
                      const status = user.account_status || 'active';
                      const statusBadge = getStatusBadge(status);
                      const isDeleted = user.is_active === false;
                      
                      return (
                        <tr key={user.user_id} className={`hover:bg-[#0a0a0f]/50 ${isDeleted ? 'opacity-60' : ''}`}>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{user.name}</p>
                                <p className="text-gray-500 text-sm">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                              user.user_type === 'employer' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {user.user_type === 'employer' ? <Briefcase className="w-3 h-3" /> : <User className="w-3 h-3" />}
                              {user.user_type}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {user.user_type === 'tradesperson' && user.profile ? (
                              <div>
                                <p className="text-white text-sm">{user.profile.trade || '-'}</p>
                                <p className="text-gray-500 text-xs flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {user.profile.work_areas?.slice(0, 2).join(', ') || '-'}
                                </p>
                              </div>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {user.is_verified ? (
                              <span className="inline-flex items-center gap-1 text-green-400 text-sm">
                                <CheckCircle className="w-4 h-4" /> Verified
                              </span>
                            ) : user.verification_pending ? (
                              <span className="inline-flex items-center gap-1 text-yellow-400 text-sm">
                                <Clock className="w-4 h-4" /> Pending
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-gray-500 text-sm">
                                <XCircle className="w-4 h-4" /> No
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded text-xs border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}>
                              {status.toUpperCase()}
                            </span>
                            {isDeleted && (
                              <span className="ml-1 px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">DELETED</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-gray-400 text-sm">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-4">
                            <button
                              onClick={() => navigate(`/admin/user/${user.user_id}`)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition text-sm"
                              data-testid={`view-user-${user.user_id}`}
                            >
                              <Eye className="w-4 h-4" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800">
                <p className="text-gray-400 text-sm">
                  Showing {offset + 1} - {Math.min(offset + limit, total)} of {total} users
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-3 py-1 bg-[#0a0a0f] border border-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                    className="px-3 py-1 bg-[#0a0a0f] border border-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <User className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No users found matching your filters</p>
              <button
                onClick={clearFilters}
                className="mt-2 text-orange-400 hover:text-orange-300 text-sm"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
